import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { openAuthenticatedBlobInNewTab } from "@/utils/openAuthenticatedBlobInNewTab";

type AuthenticatedBlobDocumentLinkProps = {
  fetchBlob: () => Promise<Blob>;
  children: ReactNode;
  className?: string;
  title?: string;
};

export function AuthenticatedBlobDocumentLink({
  fetchBlob,
  children,
  className,
  title,
}: AuthenticatedBlobDocumentLinkProps): JSX.Element {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  return (
    <button
      type="button"
      title={title}
      disabled={loading}
      onClick={() => {
        void (async () => {
          setLoading(true);
          try {
            await openAuthenticatedBlobInNewTab(fetchBlob);
          } catch {
            toast.error(t("common.documentOpenError"));
          } finally {
            setLoading(false);
          }
        })();
      }}
      className={cn(
        "inline-flex max-w-full min-w-0 items-center gap-1 text-left font-medium text-primary underline-offset-4 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
        className,
      )}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : null}
      <span className="min-w-0 truncate">{children}</span>
    </button>
  );
}
